/** Measurements — create form (RHF + zod + shadcn Form via Refine useForm). */
import { useForm } from "@refinedev/react-hook-form";
import { useList } from "@refinedev/core";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  createMeasurementSchema,
  type CreateMeasurementInput,
  type Metric,
} from "@aos/shared";
import { Form } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { PageHeader } from "@/components/layout/PageHeader";
import { FormGrid, TextField } from "../projects/_form";

/** Default a `measured_at` field to the current instant as an ISO string. */
function nowIso(): string {
  return new Date().toISOString();
}

/**
 * Coerce a number input to a finite number, mapping empty/blank input to 0.
 * Guards the required `value: z.number()` against NaN-on-empty (per the portal
 * numeric-field-submit fix) so an empty field never silently blocks submit.
 */
function numberOrZero(v: string): number {
  if (v === "" || v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** MeasurementCreate (D6) — record a measurement against a metric. */
export function MeasurementCreate() {
  const { data: metricData, isLoading: metricsLoading } = useList<Metric>({
    resource: "metrics",
    pagination: { pageSize: 200 },
  });
  const metrics = metricData?.data ?? [];

  const form = useForm<CreateMeasurementInput, any, CreateMeasurementInput>({
    resolver: zodResolver(createMeasurementSchema),
    refineCoreProps: { resource: "measurements", action: "create" },
    defaultValues: {
      metric_id: "",
      measured_at: nowIso(),
      source_system: "",
      source_artifact_id: "",
    },
  });

  const {
    refineCore: { formLoading },
    saveButtonProps,
    register,
    control,
    formState: { errors },
  } = form;

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <PageHeader
        title="Record measurement"
        subtitle="Append a new reading to a metric — measurements are immutable once recorded."
        icon="okrs"
      />
      <Form {...form}>
        <form onSubmit={saveButtonProps.onClick}>
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Measurement</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FormField
                control={control}
                name="metric_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Metric</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || undefined}
                      disabled={metricsLoading}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue
                            placeholder={
                              metricsLoading
                                ? "Loading metrics…"
                                : "Select a metric…"
                            }
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {metrics.map((m) => (
                          <SelectItem key={m.id} value={m.id}>
                            {m.name}
                            {m.unit ? ` (${m.unit})` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      The metric this reading belongs to.
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormGrid>
                <FormItem>
                  <FormLabel htmlFor="value">Value</FormLabel>
                  <FormControl>
                    <Input
                      id="value"
                      type="number"
                      step="any"
                      {...register("value", { setValueAs: numberOrZero })}
                    />
                  </FormControl>
                  {errors.value ? (
                    <p className="text-sm font-medium text-destructive">
                      {String(errors.value.message)}
                    </p>
                  ) : null}
                </FormItem>
                <TextField
                  control={control}
                  name="measured_at"
                  label="Measured at"
                  placeholder="2026-06-25T00:00:00.000Z"
                  description="ISO 8601 timestamp."
                />
              </FormGrid>

              <FormGrid>
                <TextField
                  control={control}
                  name="source_system"
                  label="Source system"
                  placeholder="manual, dashboard, etl…"
                />
                <TextField
                  control={control}
                  name="source_artifact_id"
                  label="Source artifact"
                  placeholder="art_…"
                />
              </FormGrid>
            </CardContent>
            <CardFooter className="justify-end">
              <Button type="submit" disabled={formLoading}>
                {formLoading ? "Recording…" : "Record measurement"}
              </Button>
            </CardFooter>
          </Card>
        </form>
      </Form>
    </div>
  );
}

export default MeasurementCreate;
